import { SERVER_URL } from "Constants";
import { CustomerEntity } from "Models/Entities";
import BusinessDetailEntity from "Models/Entities/BusinessDetailEntity";
import { accountType, creditReportStatus, creditReportStatusOptions } from "Models/Enums";
import { OrganisationHelper } from "Models/Helpers/OrganisationHelper";
import { store } from "Models/Store";
import { buildBlob } from "Util/CsvUtils";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import axios, { AxiosResponse } from "axios";
import classNames from "classnames";
import saveAs from "file-saver";
import moment from "moment/moment";
import { useState } from "react";
import * as uuid from 'uuid';
import { Button, Colors, Display } from "../Button/Button";
import MultiComboboxSetter from "../Combobox/MultiComboboxSetter";
import { DateRangePicker } from "../DateRangePicker/DateRangePicker";
import EntityList, { IEntityListHeaderProps } from "../EntityList/EntityList";
import MeatballMenu from "../EntityList/MeatballMenu";
import If from "../If/If";
import { confirmModal } from "../Modal/ModalUtils";
import TablePagination from "../Pagination/TablePagination";
import RequestWrap, { GetRefreshKey } from "../RequestWrap/RequestWrap";
import { TextFieldSetter } from "../TextBox/TextFieldSetter";
import { Tooltip } from "../Tooltip/Tooltip";
import { InitialGenerateCreditReportDataForCustomer } from "./GenerateCreditReport";
import CreditReportUploadModal from "./GenerateCreditReportParts/Gibs/CreditReportUploadModal";
import { AbrOrAsicSearchResponse } from "../JurisdictionBusinessEntitySearchBox/AustralianEntitySearchBox";

export type BuildGenerateCreditReportDataFromCustomer = {
	customerId: string;
	abnAcnDetail?: BusinessDetailEntity;
	trusteeDetail?: BusinessDetailEntity;
	organisationId: string;
	businessEntityId: string;
	businessEntityName: string;
	accountType: accountType;
}

export const FillInitialGenerateCreditReportDataForCustomer = (data: BuildGenerateCreditReportDataFromCustomer) => {
	const { customerId, abnAcnDetail, trusteeDetail, organisationId, businessEntityId, businessEntityName, accountType: orgAccountType } = data;
	const availableCreditReportSubjects: AbrOrAsicSearchResponse[] = [];
	if (trusteeDetail) {
		availableCreditReportSubjects.push({
			abn: "",
			acn: trusteeDetail.monitored!.acn,
			identifierNumber: trusteeDetail.monitored!.acn,
			entityName: trusteeDetail.acnName ?? "N/A",
			entityStatus: trusteeDetail.acnStatus ?? "N/A", 
			address: "N/A",
			lookUpMethod: "ACN",
		});
	}

	if (abnAcnDetail) {
		const abnValue = abnAcnDetail.monitored!.abn;
		availableCreditReportSubjects.push({
			abn: abnValue,
			acn: "",
			identifierNumber: abnValue,
			entityName: abnAcnDetail.abnName ?? "N/A",
			entityStatus:  abnAcnDetail.abnStatus ?? "N/A",
			address:abnAcnDetail.abnLocality ?? "N/A",
			lookUpMethod: "ABN",
		});
	}

	const result: InitialGenerateCreditReportDataForCustomer = {
		customerId: customerId,
		creditReportSubjects: availableCreditReportSubjects,
		creditReportBillingEntity: {
			organisationId: organisationId,
			businessEntityId: businessEntityId,
			businessEntityName: businessEntityName,
			accountType: orgAccountType,
		}
	}

	return result;
}

const fetchCreditReportListData = AwesomeDebouncePromise(async (
	search: string,
	sortColumn: string,
	sortDescending: boolean,
	page: number,
	businessEntityIds: string[],
	dateRange: Date[],
	customerId?: string,
	noPagination?: boolean
): Promise<AxiosResponse<CreditReportListProps>> => {
	const skip = page * 10;
	const startDate = dateRange.length > 0 ? dateRange[0].toDateString() : '';
	const endDate = dateRange.length === 2 ? dateRange[1].toDateString() : dateRange.length === 1 ? dateRange[0].toDateString() : '';

	if (customerId) {
		const url = `${SERVER_URL}/api/credit-report/list/${customerId}?search=${search}&startDate=${startDate}&endDate=${endDate}&orderBy=${sortColumn}&descending=${sortDescending}&skip=${skip}&take=${noPagination ? 0 : 10}`;
		return await axios.get<CreditReportListProps>(url);
	} else {
		const url = `${SERVER_URL}/api/credit-report/list?search=${search}&startDate=${startDate}&endDate=${endDate}&orderBy=${sortColumn}&descending=${sortDescending}&businessEntityIds=${businessEntityIds}&skip=${skip}&take=${noPagination ? 0 : 10}`
		return await axios.get<CreditReportListProps>(url);
	}
}, 300,
{ leading: true }
);

export type CreditReportListItemDetail = {
	id: string,
	reportDateTime: Date,
	reportType: string,
	reportProvider: string,
	entityNo: string,
	entityName: string,
	billedTo: string,
	price: number,
	generatedBy: string,
	downloadLink: string,
	status: creditReportStatus,
	requestJson: string,
	linkedCustomerId?: string,
}

export type CreditReportListProps = {
	items?: CreditReportListItemDetail[]
	total?: number
}

type Props = {
	// filter by customer for report list
	customer?: CustomerEntity;

	// if provided these will be used to pre-fill the generate credit report form
	abnAcnDetail?: BusinessDetailEntity;
	// if provided these will be used to pre-fill the generate credit report form
	trusteeDetail?: BusinessDetailEntity;
}

export const CreditReportList: React.FunctionComponent<Props> = ({
	customer,
	abnAcnDetail,
	trusteeDetail
}) => {
	const [search, setSearch] = useState('');
	const [sortColumn, setSortColumn] = useState('reportDateTime');
	const [sortDescending, setSortDescending] = useState(true);
	const [page, setPage] = useState(0);
	const [showFilters, setShowFilters] = useState(false);
	const [businessEntityIds, setBusinessEntityIds] = useState([] as string[]);
	const [dateRange, setDateRange] = useState({ value: [] } as { value: Date[] });
	const [triggerUpdate, setTriggerUpdate] = useState(0); // Increment to force the user list to refresh
	const [showGenerateCreditReportModal, setShowGenerateCreditReportModal] = useState(false);
	const [uploadReportRequestId, setUploadReportRequestId] = useState('');

	const onSortChange = (header: string) => {
		setSortColumn(header);
		setSortDescending(sortColumn === header ? !sortDescending : false);
	};

	const getColumns = (): IEntityListHeaderProps<CreditReportListItemDetail>[] => {
		return [
			{
				columnName: "id",
				displayName: "ID",
				value: item => item.id,
				hide: true
			},
			{
				columnName: "reportDateTime",
				displayName: "Date & Time",
				value: item => moment(item.reportDateTime).format('HH:mm DD/MM/YYYY'),
				sortable: true,
				sortClicked: () => {
					onSortChange('reportDateTime');
				}
			},
			{
				columnName: "reportProvider",
				displayName: "Provider",
				value: item => item.reportProvider,
				sortable: true,
				sortClicked: () => {
					onSortChange('reportProvider');
				}
			},
			{
				columnName: "reportType",
				displayName: "Report",
				value: item => item.reportType,
				sortable: true,
				sortClicked: () => {
					onSortChange('reportType');
				}
			},
			{
				columnName: "entity",
				displayName: "Entity",
				value: item => (
					<ReportEntityDetail
						entityName={item.entityName}
						entityNumber={item.entityNo}
						customerId={!customer ? item.linkedCustomerId : undefined} // Only show the customer link if not in customer view
					/>
				),
				sortable: true,
				sortClicked: () => {
					onSortChange('entity');
				},
			},
			{
				columnName: "billedTo",
				displayName: "Billed To",
				value: item => item.billedTo,
				sortable: true,
				sortClicked: () => {
					onSortChange('billedTo');
				}
			},
			{
				columnName: "price",
				displayName: "Price Inc. GST",
				value: item => `$${item.price?.toFixed(2) || '-'}`,
				sortable: true,
				sortClicked: () => {
					onSortChange('price');
				}
			},
			{
				columnName: "generatedBy",
				displayName: "Generated By",
				value: item => item.generatedBy,
				sortable: true,
				sortClicked: () => {
					onSortChange('generatedBy');
				}
			},
			{
				columnName: "status",
				displayName: "Status",
				value: item => creditReportStatusOptions[item.status],
				sortable: true,
				sortClicked: () => {
					onSortChange('status');
				}
			},
			{
				columnName: "downloadLink",
				displayName: "",
				value: item => (
					<>
						{item.status === "COMPLETED" && (
							<Button
								className="file-link client-terms"
								icon={{ icon: 'arrow-right-up', iconPos: 'icon-right' }}
								display={Display.Text}
								colors={Colors.Primary}
								onClick={() => window.open(`${SERVER_URL}/api/credit-report/view/${item.id}`)}
								buttonProps={{ style: { textDecoration: 'none' } }}
							>
								View
							</Button>
						)}
						{item.status !== "COMPLETED" && (
							<Button
								className="link-item contact-support"
								display={Display.Text}
								colors={Colors.Error}
								onClick={() => { window.open("mailto:support@accessintell.com", "_self") }}
							>
								Contact Support
							</Button>
						)}
					</>
				),
				sortable: false
			},
			{
				displayName: '',
				columnName: 'meatball-menu',
				value: item => (
					<MeatballMenu actions={[
						{
							display: 'Regenerate',
							action: () => store.routerHistory.push("credit-reports/generate", { id: item.id, stage: 'review' }),
							visible: moment(item.reportDateTime) <= moment().subtract(1, 'days')
						},
						{
							display: 'Upload Report',
							action: () => {
								setUploadReportRequestId(item.id);
								setShowGenerateCreditReportModal(true);
							},
							visible: (item.status === "ERROR" || item.status === "FAILED") && store.userType === "SUPER_USER"
						}
					]}
					/>
				),
				sortable: false,
				className: 'meatball-menu',
			}
		]
	}

	const fetchCreditReportsForExport = async () => {
		const request = await fetchCreditReportListData(
			search,
			sortColumn,
			sortDescending,
			page,
			businessEntityIds,
			dateRange.value,
			customer?.id,
			true
		);
		const creditReports: CreditReportListItemDetail[] = request.data.items ?? [];
		const result = [] as string[][];
		const columns = [
			{
				displayName: "Date & Time",
				value: (item: CreditReportListItemDetail) => moment(item.reportDateTime).format('HH:mm DD/MM/YYYY'),
			},
			{
				displayName: "Provider",
				value: (item: CreditReportListItemDetail) => item.reportProvider,
			},
			{
				displayName: "Report",
				value: (item: CreditReportListItemDetail) => item.reportType,
			},
			{
				displayName: "Entity",
				value: (item: CreditReportListItemDetail) => `${item.entityName} - ${item.entityNo}`
			},
			{
				displayName: "Billed To",
				value: (item: CreditReportListItemDetail) => item.billedTo,
			},
			{
				displayName: "Price",
				value: (item: CreditReportListItemDetail) => `$${item.price?.toFixed(2) || '-'}`,
			},
			{
				displayName: "Generated By",
				value: (item: CreditReportListItemDetail) => item.generatedBy,
			},
			{
				displayName: "Status",
				value: (item: CreditReportListItemDetail) => creditReportStatusOptions[item.status],
			}
		]
		const headers = columns.map(column => column.displayName?.toString() ?? '');
		result.push(headers);
		creditReports.forEach(creditReport => {
			result.push(
				columns.map(column => column.value(creditReport)?.toString() ?? ''),
			);
		});

		return result;
	};

	const refreshKey = GetRefreshKey(
		search,
		sortColumn,
		sortDescending,
		page,
		businessEntityIds,
		triggerUpdate,
		dateRange.value
	);
	return (
		<>
			<div className="credit-report-list">
				<div className="search-container">
					<div className="searchbar search">
						<TextFieldSetter
							className={classNames('search-input search__collection')}
							value={search}
							setValue={setSearch}
							onAfterChange={() => setPage(0)}
							label="A search for entities"
							labelVisible={false}
							placeholder="Search Credit Reports"
						/>
						{!customer &&
							<Button
								className={classNames('filter-toggle-btn', { active: showFilters })}
								icon={{ icon: 'filter-2', iconPos: 'icon-left' }}
								colors={Colors.Primary}
								display={showFilters ? Display.Solid : Display.Outline}
								onClick={() => {
									setShowFilters(show => !show);
									setBusinessEntityIds([]);
									setDateRange({ value: [] });
								}}
							>
								Filter By
							</Button>}
					</div>


					<div className="actions">
						<Button
							className="new-credit-report-btn"
							icon={{ icon: 'plus-2', iconPos: 'icon-left' }}
							display={Display.Solid}
							colors={Colors.Primary}
							onClick={() => {
								const initialGenerateCreditReportData = !!customer ? FillInitialGenerateCreditReportDataForCustomer({
									customerId: customer.id,
									abnAcnDetail: abnAcnDetail,
									trusteeDetail: trusteeDetail,
									organisationId: customer.businessEntity.organisation.id,
									businessEntityId: customer.businessEntity.id,
									businessEntityName: customer.businessEntity.name,
									accountType: customer.businessEntity.organisation.accountType,
								}) : undefined;

								return store.routerHistory.push('/hub/credit-reports/generate', {
									initialGenerateCreditReportData: initialGenerateCreditReportData,
									stage: !!customer ? 'provider' : 'countryCode',
								});
							}}
						>
							Generate credit report
						</Button>
						<Button
							className="export-button"
							colors={Colors.Alternate}
							display={Display.Outline}
							onClick={async () => {
								confirmModal(
									'Export CSV',
									'Export credit reports and save to a CSV file?',
									{
										cancelText: 'Cancel',
										confirmText: 'Export',
									},
								).then(async () => {
									const exportCreditReports = await fetchCreditReportsForExport();
									const blob = buildBlob(exportCreditReports, ',', '"');
									saveAs(blob, `exported-credit-reports-${moment().format('YYYY-MM-DD')}.csv`);
								});
							}}
						>
							Export CSV
						</Button>
					</div>
					<If condition={showFilters}>
						<div className="search-filter-section">
							<DateRangePicker
								className="date-range-picker"
								model={dateRange}
								modelProperty="value"
								placeholder="Filter by date range"
								flatpickrOptions={{
									formatDate: (date: Date) => moment.utc(date).toDate().toLocaleDateString(),
									maxDate: moment().utc().toDate(),
								}}
								dateFormat="DD/MM/YYYY"
								onAfterChange={value => {
									setDateRange({ value: value });
									setPage(0);
								}}
							>
							</DateRangePicker>
							<button
								className="click-to-clear icon-cross icon-left"
								onClick={() => setDateRange({ value: [] })}
								type="button"
								aria-label="Click to clear"
							/>
							<MultiComboboxSetter
								className="business-entity-filter"
								value={businessEntityIds}
								setValue={setBusinessEntityIds}
								getOptionValue={(value?: string) => value}
								placeholder="Business entities"
								label=""
								labelVisible={false}
								searchable
								onAfterSet={() => setPage(0)}
								options={OrganisationHelper.getBusinessOptions(store.getUser?.organisation)}
							/>
						</div>
					</If>
				</div>
				<div className="collection-component credit-reports">
					<RequestWrap
						request={() => fetchCreditReportListData(
							search,
							sortColumn,
							sortDescending,
							page,
							businessEntityIds,
							dateRange.value,
							customer?.id,
						)}
						refreshKey={refreshKey}
					>
						{request => {

							const creditReports = request.data.items ?? [];
							return (
								<>
									{creditReports.length === 0 && (
										<div className="error-container">
											<h4>No credit reports found.</h4>
										</div>
									)}
									{creditReports.length > 0 && (
										<>
											<EntityList
												collection={creditReports}
												columns={getColumns()}
												idColumn="id"
												sortColumn={sortColumn}
												sortDescending={sortDescending}
											/>
											<section className="collection__load">
												<TablePagination
													perPage={10}
													pageNo={page}
													totalRecords={request.data.total ?? 0}
													onPageChange={setPage}
												/>
											</section>
										</>
									)}
								</>
							)
						}}
					</RequestWrap>
				</div>
			</div>

			{showGenerateCreditReportModal && <CreditReportUploadModal
				reportRequestId={uploadReportRequestId}
				onClose={() => {
					setShowGenerateCreditReportModal(false);
					setTriggerUpdate(triggerUpdate + 1);
				}}
			/>}
		</>
	)
}

export type ReportEntityDetailProps = {
	entityName: string;
	entityNumber: string;
	customerId?: string;
}

const ReportEntityDetail: React.FunctionComponent<ReportEntityDetailProps> = ({
	entityName,
	entityNumber,
	customerId
}) => {
	const canAccessIntell = store.getUser?.canAccessIntel === true;
	const isLinkedToCustomer = !!customerId

	const handleOnClick = () => {
		if (!isLinkedToCustomer) return;
		if (!canAccessIntell) return;
		window.open(`/monitor/customers/${customerId}`, "_blank");
	}

	return (
		<>
			<span className={classNames({ "link-item": isLinkedToCustomer })} onClick={handleOnClick}>
				{entityName}
			</span>
			{isLinkedToCustomer && <CustomerLinkTooltip authorised={canAccessIntell} />}
			<br />
			<span>{entityNumber}</span>
		</>
	)
}

function CustomerLinkTooltip(props: {
	authorised: boolean
}) {
	const { authorised } = props;
	// Generate a unique ID for the tooltip
	// This is necessary to ensure that the tooltip can be uniquely identified
	const tooltipId = `customer-link-${uuid.v4()}`;

	const contents =
		"This credit report request is linked to your customer." +
		(!authorised ? "\n\nYou do not have permission to access Monitor Product. Contact your organisation manager to update your permissions." : "");

	return <Tooltip id={tooltipId} content={contents} place="right" />
}